import {NgModule, LOCALE_ID, inject, provideAppInitializer} from '@angular/core';
import {registerLocaleData, DecimalPipe} from '@angular/common';
import {provideHttpClient, withInterceptors, withInterceptorsFromDi} from '@angular/common/http';
import locale from '@angular/common/locales/en';
import localeDa from '@angular/common/locales/da';

import {BrowserModule, Title} from '@angular/platform-browser';
import {provideAnimations} from '@angular/platform-browser/animations';
import {ServiceWorkerModule} from '@angular/service-worker';
import {FaIconLibrary} from '@fortawesome/angular-fontawesome';
import dayjs from 'dayjs/esm';
import {NgbDateAdapter, NgbDatepickerConfig} from '@ng-bootstrap/ng-bootstrap';

import {SettingsLoaderService} from '@cupoui/core/services/settings.loader.service';
import {ApplicationConfigService} from './core/config/application-config.service';
import './config/dayjs';
import {SharedModule} from './shared/shared.module';
import {TranslationModule} from './shared/language/translation.module';
import {AppRoutingModule} from './app-routing.module';

import {AppComponent} from './app.component';
import {NgbDateDayjsAdapter} from './config/datepicker-adapter';
import {fontAwesomeIcons} from './config/font-awesome-icons';
import {FooterComponent} from './layouts/footer/footer.component';
import {PageRibbonComponent} from './layouts/profiles/page-ribbon.component';
import {ActiveMenuDirective} from './layouts/navbar/active-menu.directive';
import {SidenavComponent} from './layouts/sidenav/sidenav.component';
import {NumberFormatPipe} from './shared/pipes/number-format.pipe';
import {MainComponent} from './layouts/main/main.component';
import {LocaleService} from '@cupoui/core/services/locale.service';
import {provideNgxWebstorage, withNgxWebstorageConfig, withSessionStorage} from "ngx-webstorage";
import { provideEnvironmentNgxMask } from 'ngx-mask';
import Keycloak from "keycloak-js";
import {KEYCLOAK_CONFIG} from "@cupoui/shared/util/keycloak-runtime-config";
import {keycloakBearerInterceptor} from "@cupoui/shared/util/keycloak-interceptor";

registerLocaleData(localeDa);

@NgModule({
  declarations: [
    AppComponent,
    MainComponent,
    PageRibbonComponent,
    ActiveMenuDirective,
    FooterComponent,
    SidenavComponent,
  ],
  bootstrap: [AppComponent],
  imports: [BrowserModule,
    SharedModule,
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {enabled: false}),
    TranslationModule],
  providers: [
    Title,
    SettingsLoaderService,
    {
      provide: Keycloak,
      useFactory: () => {
        const config = inject(KEYCLOAK_CONFIG);
        return new Keycloak(config);
      }
    },
    provideAppInitializer(async () => {
      const keycloak = inject(Keycloak);
      await keycloak.init({
        onLoad: 'login-required',
        checkLoginIframe: false,
        // Ensure responseMode matches what your router tolerates
        responseMode: 'query', // or 'fragment' if using HashLocationStrategy
      });
    }),
    {
      provide: LOCALE_ID,
      useValue: LocaleService.getSavedLocale()
    },
    {provide: NgbDateAdapter, useClass: NgbDateDayjsAdapter},
    DecimalPipe,
    NumberFormatPipe,
    provideHttpClient(withInterceptors([keycloakBearerInterceptor]),withInterceptorsFromDi()),
    provideNgxWebstorage(
      withNgxWebstorageConfig({ prefix: 'cupo', separator: '-', caseSensitive: true }),
      withSessionStorage()
    ),
    provideEnvironmentNgxMask(),
    provideAnimations()
  ]
})
export class AppModule {
  constructor(
    applicationConfigService: ApplicationConfigService,
    iconLibrary: FaIconLibrary,
    dpConfig: NgbDatepickerConfig
  ) {
    registerLocaleData(locale);
    iconLibrary.addIcons(...fontAwesomeIcons);
    dpConfig.minDate = {year: dayjs().subtract(100, 'year').year(), month: 1, day: 1};
  }
}
